import React, { useState, useEffect, useRef, useImperativeHandle, forwardRef } from 'react';
import { Search, X, ChevronUp, ChevronDown } from 'lucide-react';
import '../styles/components/helpSearchBar.css';

interface HelpSearchBarProps {
  sections: string[];
  sectionRefs: Record<string, React.RefObject<HTMLElement | null>>;
  onNavigateToMatch?: (sectionId: string) => void;
  hasAuthButton?: boolean;
  inHeader?: boolean;
  inMenuBar?: boolean;
  activeSection?: string;
  onSectionClick?: (section: string) => void;
}

export interface HelpSearchBarRef {
  hasActiveSearch: () => boolean;
  navigateToNext: () => void;
  navigateToPrev: () => void;
  navigateToFirst: () => void;
  navigateToLast: () => void;
}

interface SearchMatch {
  sectionId: string;
  sectionName: string;
  element: HTMLElement;
  textNode: Text;
  offset: number;
  length: number;
}

export const HelpSearchBar = forwardRef<HelpSearchBarRef, HelpSearchBarProps>(({
  sections,
  sectionRefs,
  onNavigateToMatch,
  hasAuthButton = false,
  inHeader = false,
  inMenuBar = false,
  activeSection,
  onSectionClick
}, ref) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [matches, setMatches] = useState<SearchMatch[]>([]);
  const [currentMatchIndex, setCurrentMatchIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const highlightedElements = useRef<HTMLElement[]>([]);
  const isProgrammaticScroll = useRef(false);

  // Expose methods to parent via ref
  useImperativeHandle(ref, () => ({
    hasActiveSearch: () => highlightedElements.current.length > 0,
    navigateToNext: () => navigateMatch('next'),
    navigateToPrev: () => navigateMatch('prev'),
    navigateToFirst: () => {
      if (highlightedElements.current.length > 0) {
        setCurrentMatchIndex(0);
        updateCurrentHighlight(0, highlightedElements.current);
      }
    },
    navigateToLast: () => {
      if (highlightedElements.current.length > 0) {
        const lastIndex = highlightedElements.current.length - 1;
        setCurrentMatchIndex(lastIndex);
        updateCurrentHighlight(lastIndex, highlightedElements.current);
      }
    }
  }));

  // Clear highlights
  const clearHighlights = () => {
    highlightedElements.current.forEach(el => {
      const parent = el.parentNode;
      if (parent) {
        parent.replaceChild(document.createTextNode(el.textContent || ''), el);
        parent.normalize();
      }
    });
    highlightedElements.current = [];
  };

  // Search across all sections
  const performSearch = (term: string) => {
    clearHighlights();
    setMatches([]);
    setCurrentMatchIndex(-1);

    if (!term.trim()) return;

    const searchLower = term.toLowerCase();
    const foundHighlights: HTMLElement[] = [];

    // First pass: collect all text nodes and their matches
    const nodesToProcess: Array<{
      node: Text;
      text: string;
      matches: Array<{start: number, end: number}>;
    }> = [];

    sections.forEach(sectionId => {
      const sectionEl = sectionRefs[sectionId]?.current;
      if (!sectionEl) return;

      const walker = document.createTreeWalker(
        sectionEl,
        NodeFilter.SHOW_TEXT,
        null
      );

      let node: Text | null;
      while ((node = walker.nextNode() as Text | null)) {
        if (!node.textContent) continue;
        
        const text = node.textContent;
        const textLower = text.toLowerCase();
        
        // Find all matches in this text node
        const matches: Array<{start: number, end: number}> = [];
        let searchIndex = 0;
        while ((searchIndex = textLower.indexOf(searchLower, searchIndex)) !== -1) {
          matches.push({
            start: searchIndex,
            end: searchIndex + term.length
          });
          searchIndex += term.length;
        }

        if (matches.length > 0) {
          nodesToProcess.push({ node, text, matches });
        }
      }
    });

    // Second pass: apply highlights to all collected nodes
    nodesToProcess.forEach(({ node, text, matches }) => {
      const parent = node.parentNode;
      if (!parent) return;

      // Build the new content with highlights
      const fragments: Array<Node> = [];
      let lastEnd = 0;

      matches.forEach(match => {
        // Add text before match
        if (match.start > lastEnd) {
          fragments.push(document.createTextNode(text.substring(lastEnd, match.start)));
        }
        
        // Add highlighted match
        const highlight = document.createElement('mark');
        highlight.className = 'help-search-highlight';
        highlight.textContent = text.substring(match.start, match.end);
        highlight.setAttribute('data-search-match', 'true');
        fragments.push(highlight);
        foundHighlights.push(highlight);
        
        lastEnd = match.end;
      });

      // Add remaining text after last match
      if (lastEnd < text.length) {
        fragments.push(document.createTextNode(text.substring(lastEnd)));
      }

      // Replace the original text node with all fragments
      fragments.forEach(fragment => {
        parent.insertBefore(fragment, node);
      });
      parent.removeChild(node);
    });

    highlightedElements.current = foundHighlights;
    setMatches(foundHighlights.map((el, idx) => ({
      sectionId: '',
      sectionName: '',
      element: el,
      textNode: document.createTextNode(''), // Not used anymore
      offset: 0,
      length: 0
    })));

    if (foundHighlights.length > 0) {
      setCurrentMatchIndex(0);
      updateCurrentHighlight(0, foundHighlights);
    }
  };

  // Format section ID to readable name
  const formatSectionName = (sectionId: string): string => {
    return sectionId
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  // Update which highlight is current (just toggle classes, don't re-render)
  const updateCurrentHighlight = (index: number, highlights: HTMLElement[]) => {
    // Remove current class from all highlights
    highlights.forEach(el => {
      el.className = 'help-search-highlight';
    });

    // Add current class to the selected highlight
    if (highlights[index]) {
      highlights[index].className = 'help-search-highlight-current';
      
      // Scroll to current match (mark as programmatic to prevent keyboard dismissal)
      isProgrammaticScroll.current = true;
      
      setTimeout(() => {
        highlights[index].scrollIntoView({ behavior: 'smooth', block: 'center' });
        // Reset flag after scroll completes (longer delay for smooth scroll)
        setTimeout(() => {
          isProgrammaticScroll.current = false;
        }, 1000);
      }, 100);
    }
  };

  // Navigate to next/previous match
  const navigateMatch = (direction: 'next' | 'prev') => {
    if (highlightedElements.current.length === 0) return;
    
    let newIndex = currentMatchIndex;
    if (direction === 'next') {
      newIndex = (currentMatchIndex + 1) % highlightedElements.current.length;
    } else {
      newIndex = currentMatchIndex - 1;
      if (newIndex < 0) newIndex = highlightedElements.current.length - 1;
    }

    setCurrentMatchIndex(newIndex);
    updateCurrentHighlight(newIndex, highlightedElements.current);
  };

  // Handle search input change
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);
    performSearch(value);
  };

  // Handle expand/collapse
  const handleToggleExpand = () => {
    if (isExpanded) {
      // Collapsing - clear search
      setSearchTerm('');
      clearHighlights();
      setMatches([]);
      setCurrentMatchIndex(-1);
    }
    setIsExpanded(!isExpanded);
    
    // Focus input when expanding
    if (!isExpanded) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  };

  // Dismiss keyboard on touch (user tapping content)
  useEffect(() => {
    if (!isExpanded) return;

    const handleTouch = (e: TouchEvent) => {
      // Don't dismiss if user is tapping the input itself
      if (e.target === inputRef.current) return;
      
      // Don't dismiss if user is tapping search controls (nav buttons, close button)
      const target = e.target as HTMLElement;
      if (target.closest('.help-search-nav-btn-flat') || 
          target.closest('.help-search-close-btn-flat') ||
          target.closest('.help-search-active-section-btn-flat')) {
        return;
      }
      
      // Blur the input to dismiss keyboard when user touches content
      if (inputRef.current && document.activeElement === inputRef.current) {
        inputRef.current.blur();
      }
    };

    // Find the scroll container - matches HelpPage.tsx scroll detection logic
    const isCapacitor = document.body.classList.contains('capacitor-app');
    const scrollContainer = isCapacitor 
      ? document.querySelector('.tab-scroll-container.home-tab') as HTMLElement
      : document.querySelector('.help-page .page-template-body') as HTMLElement;

    if (scrollContainer) {
      scrollContainer.addEventListener('touchstart', handleTouch, { passive: true });
      return () => {
        scrollContainer.removeEventListener('touchstart', handleTouch);
      };
    }
  }, [isExpanded]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      clearHighlights();
    };
  }, []);

  // In menu bar mode, render elements directly without wrapper
  if (inMenuBar) {
    if (!isExpanded) {
      // Collapsed - just the search button
      return (
        <button
          className="help-search-button-flat"
          onClick={handleToggleExpand}
          aria-label="Search help content"
        >
          <Search size={20} />
        </button>
      );
    } else {
      // Expanded - render all elements as siblings
      return (
        <>
          <Search size={18} className="help-search-icon-flat" />
          <input
            ref={inputRef}
            type="text"
            className="help-search-input-flat"
            value={searchTerm}
            onChange={handleSearchChange}
            placeholder="Search help content..."
            aria-label="Search help content"
          />
          {searchTerm && (
            <>
              <div className="help-search-divider-flat">|</div>
              {activeSection && onSectionClick && (
                <button
                  className="help-search-active-section-btn-flat"
                  onClick={() => onSectionClick(activeSection)}
                >
                  {formatSectionName(activeSection)}
                </button>
              )}
              <div className="help-search-results-info-flat">
                {matches.length > 0 ? (
                  <>{currentMatchIndex + 1} of {matches.length}</>
                ) : (
                  <>No results</>
                )}
              </div>
              {matches.length > 0 && (
                <>
                  <button
                    className="help-search-nav-btn-flat"
                    onClick={() => navigateMatch('prev')}
                    aria-label="Previous match"
                  >
                    <ChevronUp size={16} />
                  </button>
                  <button
                    className="help-search-nav-btn-flat"
                    onClick={() => navigateMatch('next')}
                    aria-label="Next match"
                  >
                    <ChevronDown size={16} />
                  </button>
                </>
              )}
            </>
          )}
          <button
            className="help-search-close-btn-flat"
            onClick={handleToggleExpand}
            aria-label="Close search"
          >
            <X size={18} />
          </button>
        </>
      );
    }
  }

  // Original floating mode for non-menu-bar usage
  return (
    <div className={`help-search-bar ${isExpanded ? 'expanded' : 'collapsed'} ${hasAuthButton ? 'with-auth-button' : ''} ${inHeader ? 'in-header' : ''}`}>
      {!isExpanded ? (
        <button
          className="help-search-button"
          onClick={handleToggleExpand}
          aria-label="Search help content"
        >
          <Search size={20} />
          <span className="help-search-button-text">Search...</span>
        </button>
      ) : (
        <div className="help-search-expanded">
          <Search size={18} className="help-search-icon" />
          <input
            ref={inputRef}
            type="text"
            className="help-search-input"
            value={searchTerm}
            onChange={handleSearchChange}
            placeholder="Search help content..."
            aria-label="Search help content"
          />
          {searchTerm && (
            <>
              <div className="help-search-divider">|</div>
              <div className="help-search-results-info">
                {matches.length > 0 ? (
                  <>{currentMatchIndex + 1} of {matches.length}</>
                ) : (
                  <>No results</>
                )}
              </div>
              {matches.length > 0 && (
                <>
                  <button
                    className="help-search-nav-btn"
                    onClick={() => navigateMatch('prev')}
                    aria-label="Previous match"
                  >
                    <ChevronUp size={16} />
                  </button>
                  <button
                    className="help-search-nav-btn"
                    onClick={() => navigateMatch('next')}
                    aria-label="Next match"
                  >
                    <ChevronDown size={16} />
                  </button>
                </>
              )}
            </>
          )}
          <button
            className="help-search-close-btn"
            onClick={handleToggleExpand}
            aria-label="Close search"
          >
            <X size={18} />
          </button>
        </div>
      )}
    </div>
  );
});
